import { FocusEventHandler, forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import t from "../../i18n";
import useCombinedRefs from "../../use/combined-refs";
import useTextMeasure from "../../use/text-measure";
import { classNames } from "../../utils";
import { siftClassNames } from "../../utils/tailwind";
import LoadingSpinner from "../ui/LoadingSpinner";

export type InputProps = JSX.IntrinsicElements["input"] & {
  className?: string;
  ignoreValueOnFocus?: boolean;
  loading?: boolean;
  prefix?: string;
  suffix?: string;
  trimPrefix?: boolean;
  trimSuffix?: boolean;
  validation?: Exclude<Validation, "email">;
  validationTrigger?: ValidationTrigger;
};

type Validation = "email" | "multiEmail" | "name" | "phoneNumber" | "secretKey" | "url" | "usZipCode";

type ValidationTrigger = "blur" | "submit";

const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  let {
    className,
    ignoreValueOnFocus,
    loading,
    onBlur,
    onFocus,
    prefix: prefixProp,
    suffix: suffixProp,
    trimPrefix,
    trimSuffix,
    validationTrigger = "submit",
    ...inputProps
  } = props;
  const stringValueProp = String(props.value ?? "");
  const inputRef = useCombinedRefs<HTMLInputElement>(ref);
  const [currentValue, setCurrentValue] = useState<string>(stringValueProp);
  const [hasFocus, setHasFocus] = useState(false);

  const prefix = prefixProp && (!currentValue.startsWith(prefixProp) || !trimPrefix) ? prefixProp : undefined;
  const suffix = suffixProp && (!currentValue.endsWith(suffixProp) || !trimSuffix) ? suffixProp : undefined;

  const [outerClassName, innerClassName] = siftClassNames(className, ["margin", "layout"]);

  const prefixLength = useTextMeasure("text-sm", prefix);
  const valueProp = ignoreValueOnFocus && hasFocus ? currentValue : props.value;
  const valueLength = useTextMeasure(
    "text-sm",
    suffix?.startsWith(" ") ? `${valueProp ?? currentValue} ` : String(valueProp ?? currentValue),
  );

  const resetValidationMessage = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    input.setAttribute("pattern", ".*");
    input.setCustomValidity("");
  }, [inputRef]);

  useEffect(() => {
    const interval = setInterval(() => {
      if ((!hasFocus || !ignoreValueOnFocus) && inputRef.current && inputRef.current.value !== currentValue)
        setCurrentValue(inputRef.current.value);
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [currentValue, inputRef, hasFocus, ignoreValueOnFocus]);

  const outerOnChange = props.onChange;
  const innerOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      resetValidationMessage();
      setCurrentValue(e.target.value);
      outerOnChange?.(e);
    },
    [outerOnChange, resetValidationMessage],
  );

  const innerOnBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    (...params) => {
      setHasFocus(false);
      onBlur?.(...params);
    },
    [onBlur],
  );

  const innerOnFocus: FocusEventHandler<HTMLInputElement> = useCallback(
    (...params) => {
      if (inputProps.readOnly) return;
      setHasFocus(true);
      onFocus?.(...params);
    },
    [onFocus, inputProps.readOnly],
  );

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    if (!hasFocus) {
      const validation = getValidation({ type: props.type, validation: props.validation });
      if (!validation) return;
      validateInput({ input, validation });
    }

    if (validationTrigger === "blur") input.reportValidity();
  }, [hasFocus, validationTrigger, inputRef, props.type, props.validation]);

  useEffect(() => {
    const input = inputRef.current;
    const validation = getValidation({ type: props.type, validation: props.validation });

    if (!validation || !input) return;

    const handleSubmitBefore = (e: SubmitEvent) => {
      // Only validate if the input belongs to the form being submitted
      if (input.form !== e.target) return;
      if (!validateInput({ input, validation })) {
        e.stopPropagation();
        e.preventDefault();
        input.reportValidity();
      }
    };

    document.addEventListener("submit", handleSubmitBefore, true);

    return () => {
      document.removeEventListener("submit", handleSubmitBefore, true);
    };
  }, [props.type, props.validation, inputRef]);

  useEffect(() => {
    if (hasFocus && inputProps.type === "number") {
      const listener = () => {
        inputRef.current?.blur();
      };

      document.addEventListener("wheel", listener);

      return () => {
        document.removeEventListener("wheel", listener);
      };
    }
  }, [inputRef, hasFocus, inputProps.type]);

  const paddingLeft = useMemo(() => {
    if (!prefix) return undefined;

    return prefixLength + 16;
  }, [prefix, prefixLength]);

  return (
    <div className={classNames("relative flex rounded-md shadow-sm", outerClassName)}>
      {prefix && (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
          <span className="text-sm text-gray-500">{prefix}</span>
        </div>
      )}
      <input
        className={classNames(
          `block max-w-full grow border border-gray-200 bg-white p-2 px-4 text-sm transition duration-100 ease-in-out rounded-md
          disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-50 dark:text-white dark:bg-gray-800
          focus:border-primary dark:focus:border-secondary focus:ring-primary dark:focus:ring-secondary focus:ring-1
          dark:border-gray-700 group-[.has-error]:border-red-300 group-[.has-error]:pr-10 group-[.has-error]:text-red-900
          group-[.has-error]:placeholder-red-300 group-[.has-error]:focus:border-red-500 group-[.has-error]:focus:outline-none
          group-[.has-error]:focus:ring-red-500 group-[.has-error]:dark:border-red-500 group-[.has-error]:dark:text-red-200
          group-[.has-error]:dark:focus:border-red-500 read-only:cursor-default read-only:focus:border-gray-200
          read-only:focus:ring-0 dark:read-only:focus:border-gray-700`,
          innerClassName,
        )}
        ref={inputRef}
        style={{
          paddingLeft,
        }}
        {...inputProps}
        onBlur={innerOnBlur}
        onChange={innerOnChange}
        onFocus={innerOnFocus}
        value={valueProp}
      />
      {suffix && (
        <div
          className="pointer-events-none absolute inset-0 flex items-center overflow-hidden"
          style={{
            paddingLeft: `${(paddingLeft ?? 16) + valueLength + 1}px`, // + 1 because it looks like shit otherwise. I don't know why.
          }}
        >
          <span className="text-sm text-gray-500">{suffix}</span>
        </div>
      )}
      {!suffix && (
        <div className="pointer-events-none absolute inset-y-0 right-0 hidden items-center pr-3 group-[.has-error]:flex">
          <svg
            className="size-5 text-red-500"
            fill="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clipRule="evenodd"
              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
              fillRule="evenodd"
            />
          </svg>
        </div>
      )}
      {loading && (
        <div className="absolute right-2 h-full w-5">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
});

function generateValidation(validation: Validation) {
  let regex: RegExp, message: string;

  switch (validation) {
    case "email":
      regex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
      message = t("Please enter a valid email address.");
      break;
    case "multiEmail":
      regex = /^([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})(,\s*[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})*$/i;
      message = t("Please enter valid email addresses separated by commas.");
      break;
    case "name":
      regex = /^[^0-9`!@#$%\^&*<>+_\/=:]+$/;
      message = t("Only use letters in your name");
      break;
    case "phoneNumber":
      regex = /^[0-9+\(\)\s-]*$/;
      message = t("Please enter a valid phone number.");
      break;
    case "secretKey":
      regex = /^.{32,}$/i;
      message = t("Secret key must be at least 32 characters long");
      break;
    case "url":
      regex = /^https?:\/\//;
      message = t("Url must start with http:// or https://");
      break;
    case "usZipCode":
      regex = /^\d{5}$/i;
      message = t("US ZIP code must be 5 digits");
      break;
    default:
      validation satisfies never;
      throw new Error("unknown validation");
  }

  return {
    message,
    regex,
  };
}

function getValidation({
  type,
  validation,
}: {
  type: InputProps["type"];
  validation: InputProps["validation"];
}): undefined | Validation {
  return type === "email" ? "email" : validation;
}

function validateInput({ input, validation }: { input: HTMLInputElement; validation: Validation }): boolean {
  const { message, regex } = generateValidation(validation);

  if (input.value.length > 0 && !regex.test(input.value)) {
    input.setCustomValidity(message);
    input.setAttribute("pattern", "\s{0}");

    return false;
  }

  return true;
}

export default Input;
